'use client';

import { useState, useEffect, useRef } from 'react';
import QRCodeStyling from 'qr-code-styling';
import { EkutuQrLogo } from '@/components/EkutuQrLogo';
import {
	Globe,
	Upload,
	Check,
	Clock,
	Users,
	ChevronDown,
	Contact,
	Link2,
	Smartphone,
	QrCode,
	UtensilsCrossed,
	Phone,
	Mail,
	Share2,
	Star,
	Sparkles,
	ArrowRight,
	Music,
	ExternalLink,
} from 'lucide-react';
import { Link } from '@/i18n/navigation';

const COLOR_OPTIONS = [
	{ id: 'emerald', label: 'Emerald', hex: '#0f766e', bg: 'bg-[#0f766e]' },
	{ id: 'black', label: 'Slate Black', hex: '#0f172a', bg: 'bg-[#0f172a]' },
	{ id: 'blue', label: 'Ekutu Blue', hex: '#2563eb', bg: 'bg-[#2563eb]' },
	{ id: 'purple', label: 'Violet', hex: '#7c3aed', bg: 'bg-[#7c3aed]' },
] as const;

type ColorId = (typeof COLOR_OPTIONS)[number]['id'];

export function HeroDualShowcase() {
	const [selectedColor, setSelectedColor] = useState<ColorId>('emerald');
	const [activeStyle, setActiveStyle] = useState<'rounded' | 'default'>('rounded');
	const [qrSize, setQrSize] = useState<number>(512);

	// Mobile interactive tab state
	const [mobileActiveTab, setMobileActiveTab] = useState<'experiences' | 'qr'>('experiences');
	const [experienceType, setExperienceType] = useState<'vcard' | 'menu' | 'social'>('vcard');

	// QR Code DOM refs and instances
	const desktopQrRef = useRef<HTMLDivElement>(null);
	const mobileQrRef = useRef<HTMLDivElement>(null);
	const [desktopQrInstance, setDesktopQrInstance] = useState<QRCodeStyling | null>(null);
	const [mobileQrInstance, setMobileQrInstance] = useState<QRCodeStyling | null>(null);

	const currentColorHex = COLOR_OPTIONS.find((c) => c.id === selectedColor)?.hex || '#0f766e';

	// Helper to instantiate QRCodeStyling
	const createQrCode = (dimension: number) => {
		return new QRCodeStyling({
			width: dimension,
			height: dimension,
			type: 'svg',
			data: 'https://qrcode.ekutulimited.com',
			dotsOptions: {
				color: currentColorHex,
				type: activeStyle === 'rounded' ? 'rounded' : 'square',
			},
			cornersSquareOptions: {
				color: currentColorHex,
				type: activeStyle === 'rounded' ? 'extra-rounded' : 'square',
			},
			cornersDotOptions: {
				color: currentColorHex,
				type: activeStyle === 'rounded' ? 'dot' : 'square',
			},
			backgroundOptions: {
				color: '#ffffff',
			},
			imageOptions: {
				crossOrigin: 'anonymous',
				margin: 3,
				imageSize: 0.22,
			},
		});
	};

	// Mount desktop QR instance
	useEffect(() => {
		const qr = createQrCode(190);
		setDesktopQrInstance(qr);
		if (desktopQrRef.current) {
			desktopQrRef.current.innerHTML = '';
			qr.append(desktopQrRef.current);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Mount mobile QR instance when mobile QR tab becomes active
	useEffect(() => {
		if (mobileActiveTab === 'qr' && mobileQrRef.current) {
			const qr = createQrCode(190);
			setMobileQrInstance(qr);
			mobileQrRef.current.innerHTML = '';
			qr.append(mobileQrRef.current);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mobileActiveTab]);

	// Live updates when style or color changes
	useEffect(() => {
		const updateOptions = {
			dotsOptions: {
				color: currentColorHex,
				type: (activeStyle === 'rounded' ? 'rounded' : 'square') as any,
			},
			cornersSquareOptions: {
				color: currentColorHex,
				type: (activeStyle === 'rounded' ? 'extra-rounded' : 'square') as any,
			},
			cornersDotOptions: {
				color: currentColorHex,
				type: (activeStyle === 'rounded' ? 'dot' : 'square') as any,
			},
		};

		if (desktopQrInstance) {
			desktopQrInstance.update(updateOptions);
		}
		if (mobileQrInstance) {
			mobileQrInstance.update(updateOptions);
		}
	}, [desktopQrInstance, mobileQrInstance, currentColorHex, activeStyle]);

	return (
		<div className="w-full max-w-[1240px] mx-auto">
			{/* ============================================================== */}
			{/* MOBILE & TABLET VIEW (< 1024px)                                 */}
			{/* Interactive Segmented Showcase with full-scale phone & QR      */}
			{/* ============================================================== */}
			<div className="lg:hidden flex flex-col items-center">
				{/* Segmented Control Bar */}
				<div className="w-full max-w-sm flex items-center p-1 rounded-2xl bg-slate-100/90 border border-slate-200/90 mb-5 shadow-2xs">
					<button
						type="button"
						onClick={() => setMobileActiveTab('experiences')}
						className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
							mobileActiveTab === 'experiences'
								? 'bg-white text-slate-900 shadow-xs border border-slate-200/80'
								: 'text-slate-600 hover:text-slate-900'
						}`}
					>
						<Smartphone className="size-3.5 text-[#0f766e]" />
						<span>Live Experiences</span>
					</button>
					<button
						type="button"
						onClick={() => setMobileActiveTab('qr')}
						className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
							mobileActiveTab === 'qr'
								? 'bg-white text-slate-900 shadow-xs border border-slate-200/80'
								: 'text-slate-600 hover:text-slate-900'
						}`}
					>
						<QrCode className="size-3.5 text-[#0f766e]" />
						<span>Custom QR Studio</span>
					</button>
				</div>

				{/* TAB 1: Mobile Experiences Showcase */}
				{mobileActiveTab === 'experiences' && (
					<div className="w-full max-w-md bg-white border border-slate-200/90 rounded-3xl p-5 shadow-sm text-left flex flex-col items-center">
						{/* Sub-Pills for Experience Switching */}
						<div className="w-full flex items-center justify-center gap-1.5 p-1 bg-slate-50 border border-slate-200/80 rounded-2xl mb-5">
							<button
								type="button"
								onClick={() => setExperienceType('vcard')}
								className={`flex-1 py-1.5 px-2 rounded-xl text-[11px] font-semibold flex items-center justify-center gap-1 transition-all cursor-pointer ${
									experienceType === 'vcard'
										? 'bg-white text-slate-900 shadow-2xs border border-slate-200 font-bold'
										: 'text-slate-500 hover:text-slate-800'
								}`}
							>
								<Contact className="size-3 text-[#0f766e]" />
								<span>Business Card</span>
							</button>
							<button
								type="button"
								onClick={() => setExperienceType('menu')}
								className={`flex-1 py-1.5 px-2 rounded-xl text-[11px] font-semibold flex items-center justify-center gap-1 transition-all cursor-pointer ${
									experienceType === 'menu'
										? 'bg-white text-slate-900 shadow-2xs border border-slate-200 font-bold'
										: 'text-slate-500 hover:text-slate-800'
								}`}
							>
								<UtensilsCrossed className="size-3 text-amber-600" />
								<span>Menu</span>
							</button>
							<button
								type="button"
								onClick={() => setExperienceType('social')}
								className={`flex-1 py-1.5 px-2 rounded-xl text-[11px] font-semibold flex items-center justify-center gap-1 transition-all cursor-pointer ${
									experienceType === 'social'
										? 'bg-white text-slate-900 shadow-2xs border border-slate-200 font-bold'
										: 'text-slate-500 hover:text-slate-800'
								}`}
							>
								<Link2 className="size-3 text-blue-600" />
								<span>Social Bio</span>
							</button>
						</div>

						{/* Full-Scale iPhone Mockup (270px wide with high readability) */}
						<div className="w-[270px] xs:w-[285px] bg-slate-950 p-2.5 rounded-[2.5rem] shadow-xl border border-slate-800/80 relative">
							<div className="bg-white rounded-[2rem] overflow-hidden flex flex-col aspect-[9/18.5] text-center relative border border-slate-100">
								{/* Dynamic Island Status Bar */}
								<div className="h-6 px-4 pt-1.5 flex items-center justify-between text-slate-800 text-[10px] font-semibold bg-white shrink-0">
									<span>9:41</span>
									{/* Modern Dynamic Island pill */}
									<div className="w-16 h-3.5 bg-black rounded-full mx-auto -mt-1 flex items-center justify-end pr-1.5">
										<div className="size-1.5 rounded-full bg-slate-900" />
									</div>
									<div className="flex items-center gap-1">
										<span className="text-[8.5px]">●●●</span>
										<span className="text-[9px]">🔋</span>
									</div>
								</div>

								{/* Screen Content: Business Card View */}
								{experienceType === 'vcard' && (
									<div className="flex flex-col flex-1 overflow-hidden">
										{/* Banner */}
										<div className="bg-gradient-to-r from-[#0f766e] to-[#047857] text-white pt-2.5 pb-7 px-3 shrink-0">
											<div className="text-[10px] font-bold tracking-wider uppercase opacity-90">
												Digital Business Card
											</div>
										</div>

										{/* Avatar & Bio */}
										<div className="relative -mt-6 px-3.5 flex flex-col items-center flex-1">
											<div className="size-14 rounded-full border-2 border-white bg-slate-100 overflow-hidden shadow-md mb-2 shrink-0">
												{/* eslint-disable-next-line @next/next/no-img-element */}
												<img
													src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=300&q=80"
													alt="Sarah Jenkins"
													className="size-full object-cover"
												/>
											</div>
											<div className="text-sm font-bold text-slate-900 leading-tight">
												Sarah Jenkins
											</div>
											<div className="text-[10px] font-medium text-slate-600 mt-0.5">
												VP of Marketing • Acme Corp
											</div>
											<div className="text-[9px] text-slate-400 mt-0.5 mb-2.5">
												San Francisco, CA
											</div>

											{/* Contact Quick Buttons */}
											<div className="w-full space-y-1.5 mb-2.5">
												<div className="w-full py-1.5 px-3 rounded-xl bg-slate-50 border border-slate-200/80 text-[10px] font-medium text-slate-800 flex items-center gap-2 shadow-2xs">
													<Phone className="size-3 text-[#0f766e]" />
													<span className="truncate">+1 (415) 555-0192</span>
												</div>
												<div className="w-full py-1.5 px-3 rounded-xl bg-slate-50 border border-slate-200/80 text-[10px] font-medium text-slate-800 flex items-center gap-2 shadow-2xs">
													<Mail className="size-3 text-[#0f766e]" />
													<span className="truncate">sarah.j@acmecorp.com</span>
												</div>
												<div className="w-full py-1.5 px-3 rounded-xl bg-slate-50 border border-slate-200/80 text-[10px] font-medium text-slate-800 flex items-center gap-2 shadow-2xs">
													<Globe className="size-3 text-[#0f766e]" />
													<span className="truncate">acmecorp.com</span>
												</div>
											</div>

											{/* Primary Action Buttons */}
											<div className="w-full space-y-1.5 mt-auto mb-2">
												<button
													type="button"
													className="w-full py-2 rounded-xl bg-[#0f766e] text-white text-[11px] font-bold shadow-2xs flex items-center justify-center gap-1.5"
												>
													<Share2 className="size-3" />
													<span>Share Contact Back</span>
												</button>
												<button
													type="button"
													className="w-full py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-[10px] font-semibold transition"
												>
													Save to Contacts (.vcf)
												</button>
											</div>

											<div className="text-[8px] text-slate-400 pb-1">Powered by Ekutu QR</div>
										</div>
									</div>
								)}

								{/* Screen Content: Restaurant Menu View */}
								{experienceType === 'menu' && (
									<div className="flex flex-col flex-1 overflow-hidden text-left">
										{/* Banner with Restaurant Header */}
										<div className="relative h-18 w-full overflow-hidden shrink-0">
											{/* eslint-disable-next-line @next/next/no-img-element */}
											<img
												src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=500&q=80"
												alt="The Olive Bistro"
												className="size-full object-cover"
											/>
											<div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-2.5 text-white">
												<div className="text-xs font-bold leading-tight">The Olive Bistro</div>
												<div className="text-[8.5px] text-emerald-300 font-medium flex items-center gap-1 mt-0.5">
													<Star className="size-2.5 fill-current text-amber-400" />
													<span>4.9 (420+ reviews) • Italian Fine Dining</span>
												</div>
											</div>
										</div>

										{/* Food Menu Items */}
										<div className="p-2.5 space-y-2 flex-1 overflow-hidden">
											<div className="text-[10px] font-bold text-slate-800 uppercase tracking-wider pb-0.5 border-b border-slate-100">
												Chef's Specials
											</div>

											{/* Dish 1 */}
											<div className="flex items-center gap-2 pb-1.5 border-b border-slate-100">
												<div className="size-9 rounded-lg overflow-hidden shrink-0 bg-slate-100">
													{/* eslint-disable-next-line @next/next/no-img-element */}
													<img
														src="https://images.unsplash.com/photo-1551183053-bf91a1d81141?auto=format&fit=crop&w=160&q=80"
														alt="Truffle Tagliatelle"
														className="size-full object-cover"
													/>
												</div>
												<div className="min-w-0 flex-1">
													<div className="text-[10px] font-bold text-slate-900 truncate">
														Truffle Tagliatelle
													</div>
													<div className="text-[8px] text-slate-500 truncate">
														Handmade pasta, black winter truffle
													</div>
												</div>
												<span className="text-[10px] font-extrabold text-[#0f766e] shrink-0">
													$24.00
												</span>
											</div>

											{/* Dish 2 */}
											<div className="flex items-center gap-2 pb-1.5 border-b border-slate-100">
												<div className="size-9 rounded-lg overflow-hidden shrink-0 bg-slate-100">
													{/* eslint-disable-next-line @next/next/no-img-element */}
													<img
														src="https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=160&q=80"
														alt="Burrata Caprese"
														className="size-full object-cover"
													/>
												</div>
												<div className="min-w-0 flex-1">
													<div className="text-[10px] font-bold text-slate-900 truncate">
														Burrata Caprese
													</div>
													<div className="text-[8px] text-slate-500 truncate">
														Heirloom tomatoes, aged balsamic
													</div>
												</div>
												<span className="text-[10px] font-extrabold text-[#0f766e] shrink-0">
													$17.50
												</span>
											</div>

											{/* Dish 3 */}
											<div className="flex items-center gap-2">
												<div className="size-9 rounded-lg overflow-hidden shrink-0 bg-slate-100">
													{/* eslint-disable-next-line @next/next/no-img-element */}
													<img
														src="https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=160&q=80"
														alt="Artisanal Pizza"
														className="size-full object-cover"
													/>
												</div>
												<div className="min-w-0 flex-1">
													<div className="text-[10px] font-bold text-slate-900 truncate">
														Wood-Fired Margherita
													</div>
													<div className="text-[8px] text-slate-500 truncate">
														San Marzano, fior di latte
													</div>
												</div>
												<span className="text-[10px] font-extrabold text-[#0f766e] shrink-0">
													$19.00
												</span>
											</div>
										</div>

										{/* Order CTA */}
										<div className="p-2.5 pt-0 mt-auto">
											<button
												type="button"
												className="w-full py-2 rounded-xl bg-[#0f766e] text-white text-[10px] font-bold shadow-2xs text-center"
											>
												View Full Menu & Order
											</button>
										</div>
									</div>
								)}

								{/* Screen Content: Social Links View */}
								{experienceType === 'social' && (
									<div className="flex flex-col flex-1 overflow-hidden text-center p-3">
										{/* Creator Header */}
										<div className="size-13 rounded-full border-2 border-[#0f766e]/30 overflow-hidden mx-auto mb-1.5 shadow-sm">
											{/* eslint-disable-next-line @next/next/no-img-element */}
											<img
												src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=300&q=80"
												alt="Alex Rivera"
												className="size-full object-cover"
											/>
										</div>
										<div className="text-xs font-bold text-slate-900 leading-tight">
											Alex Rivera
										</div>
										<div className="text-[9px] text-slate-500 mb-3">
											Creator & Digital Strategist
										</div>

										{/* Social Links Cards */}
										<div className="space-y-1.5 flex-1">
											<div className="w-full py-2 px-3 rounded-xl bg-slate-50 border border-slate-200/80 text-[10px] font-semibold text-slate-800 flex items-center justify-between shadow-2xs">
												<span className="flex items-center gap-1.5">
													<span className="size-2 rounded-full bg-pink-500" />
													<span>Instagram (@alexrivera)</span>
												</span>
												<ExternalLink className="size-2.5 text-slate-400" />
											</div>
											<div className="w-full py-2 px-3 rounded-xl bg-slate-50 border border-slate-200/80 text-[10px] font-semibold text-slate-800 flex items-center justify-between shadow-2xs">
												<span className="flex items-center gap-1.5">
													<span className="size-2 rounded-full bg-red-500" />
													<span>YouTube Channel</span>
												</span>
												<ExternalLink className="size-2.5 text-slate-400" />
											</div>
											<div className="w-full py-2 px-3 rounded-xl bg-slate-50 border border-slate-200/80 text-[10px] font-semibold text-slate-800 flex items-center justify-between shadow-2xs">
												<span className="flex items-center gap-1.5">
													<span className="size-2 rounded-full bg-black" />
													<span>TikTok (@alexcreates)</span>
												</span>
												<ExternalLink className="size-2.5 text-slate-400" />
											</div>
											<div className="w-full py-2 px-3 rounded-xl bg-slate-50 border border-slate-200/80 text-[10px] font-semibold text-slate-800 flex items-center justify-between shadow-2xs">
												<span className="flex items-center gap-1.5">
													<Music className="size-3 text-emerald-600" />
													<span>Spotify Playlist</span>
												</span>
												<ExternalLink className="size-2.5 text-slate-400" />
											</div>
										</div>

										<div className="text-[8px] text-slate-400 pt-2 pb-0.5">Powered by Ekutu QR</div>
									</div>
								)}

								{/* Home Indicator */}
								<div className="w-12 h-1 bg-slate-300 rounded-full mx-auto mb-1 shrink-0" />
							</div>
						</div>

						{/* Explanatory Caption */}
						<p className="text-[11px] text-slate-500 text-center mt-3 font-medium max-w-xs">
							📱 Live interactive experience. When scanned, your QR code opens this mobile landing
							page instantly with zero apps required.
						</p>

						{/* Direct Link to Experience Creator */}
						<Link
							href="/create"
							className="w-full mt-3.5 py-2.5 px-4 rounded-xl bg-[#0f766e] hover:bg-[#115e59] active:bg-[#134e4a] text-white text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-sm active:scale-[0.98]"
						>
							<Sparkles className="size-3.5" />
							<span>Create Free QR Code</span>
							<ArrowRight className="size-3.5 ml-0.5" />
						</Link>
					</div>
				)}

				{/* TAB 2: Mobile Custom QR Studio */}
				{mobileActiveTab === 'qr' && (
					<div className="w-full max-w-md bg-white border border-slate-200/90 rounded-3xl p-5 shadow-sm text-center flex flex-col items-center">
						<div className="mb-4">
							<h3 className="text-base font-bold text-slate-900">Custom Branded QR Code</h3>
							<p className="text-xs text-slate-500 mt-0.5">
								Interactive live preview with real-time styling
							</p>
						</div>

						{/* Center Scannable QR Code */}
						<div className="bg-white rounded-3xl p-4 shadow-md border border-slate-200/90 flex flex-col items-center justify-center aspect-square max-w-[210px] relative overflow-hidden mb-4">
							<div
								ref={mobileQrRef}
								className="w-[170px] h-[170px] flex items-center justify-center [&>svg]:w-full [&>svg]:h-full"
							/>
							<div className="absolute size-8 rounded-lg bg-white shadow-md border border-slate-100 flex items-center justify-center p-1 pointer-events-none">
								<EkutuQrLogo size="sm" showText={false} />
							</div>
						</div>

						<p className="text-[11px] text-slate-500 font-medium mb-4">
							Point phone camera to test live redirect to Ekutu QR
						</p>

						{/* Color Picker Chips */}
						<div className="w-full max-w-xs mb-3">
							<label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 text-left">
								Select Color
							</label>
							<div className="flex items-center justify-center gap-2">
								{COLOR_OPTIONS.map((color) => (
									<button
										key={color.id}
										type="button"
										onClick={() => setSelectedColor(color.id)}
										className={`flex-1 h-8 rounded-xl ${color.bg} flex items-center justify-center transition-all cursor-pointer ${
											selectedColor === color.id
												? 'ring-2 ring-offset-2 ring-slate-900 shadow-xs scale-105'
												: 'opacity-80 hover:opacity-100'
										}`}
										title={color.label}
									>
										{selectedColor === color.id && (
											<Check className="size-3.5 text-white stroke-[3]" />
										)}
									</button>
								))}
							</div>
						</div>

						{/* Corner Style Chips */}
						<div className="w-full max-w-xs mb-4">
							<label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 text-left">
								Corner Pattern
							</label>
							<div className="flex items-center gap-2">
								<button
									type="button"
									onClick={() => setActiveStyle('rounded')}
									className={`flex-1 py-1.5 px-3 rounded-xl text-xs font-semibold transition border cursor-pointer ${
										activeStyle === 'rounded'
											? 'bg-slate-900 text-white border-slate-900 shadow-2xs'
											: 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
									}`}
								>
									Rounded
								</button>
								<button
									type="button"
									onClick={() => setActiveStyle('default')}
									className={`flex-1 py-1.5 px-3 rounded-xl text-xs font-semibold transition border cursor-pointer ${
										activeStyle === 'default'
											? 'bg-slate-900 text-white border-slate-900 shadow-2xs'
											: 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
									}`}
								>
									Classic
								</button>
							</div>
						</div>

						{/* 1-Tap CTA to Full Creator */}
						<Link
							href="/create"
							className="w-full py-2.5 px-4 rounded-xl bg-[#0f766e] hover:bg-[#115e59] active:bg-[#134e4a] text-white text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-sm active:scale-[0.98]"
						>
							<Sparkles className="size-3.5" />
							<span>Create Free QR Code</span>
							<ArrowRight className="size-3.5 ml-0.5" />
						</Link>
					</div>
				)}
			</div>

			{/* ============================================================== */}
			{/* DESKTOP VIEW (≥ 1024px)                                        */}
			{/* Full 2-Column Side-by-Side Dual Showcase preserved untouched   */}
			{/* ============================================================== */}
			<div className="hidden lg:grid grid-cols-2 gap-8 items-stretch text-left">
				{/* Left Card: Design Your Custom QR Code */}
				<div className="bg-white border border-slate-200/90 rounded-3xl p-7 shadow-sm flex flex-col justify-between">
					<div>
						{/* Card Title */}
						<h2 className="text-2xl font-bold text-slate-900 mb-6">Design Your Custom QR Code</h2>

						<div className="grid grid-cols-12 gap-6 items-center">
							{/* Controls Column */}
							<div className="col-span-6 space-y-4">
								{/* Type Selector */}
								<div>
									<label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
										Type
									</label>
									<div className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-slate-50/80 border border-slate-200 text-xs font-semibold text-slate-800 shadow-2xs">
										<span className="flex items-center gap-2">
											<Globe className="size-3.5 text-[#0f766e]" />
											<span>Web URL</span>
										</span>
										<ChevronDown className="size-3.5 text-slate-400" />
									</div>
								</div>

								{/* Color Swatches */}
								<div>
									<label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
										Color
									</label>
									<div className="flex items-center gap-2.5">
										{COLOR_OPTIONS.map((color) => (
											<button
												key={color.id}
												type="button"
												onClick={() => setSelectedColor(color.id)}
												className={`h-7 w-10 rounded-lg ${color.bg} flex items-center justify-center transition-all cursor-pointer ${
													selectedColor === color.id
														? 'ring-2 ring-offset-2 ring-slate-900 shadow-xs'
														: 'opacity-85 hover:opacity-100'
												}`}
												title={color.label}
											>
												{selectedColor === color.id && (
													<Check className="size-3 text-white stroke-[3]" />
												)}
											</button>
										))}
									</div>
								</div>

								{/* Logo Upload */}
								<div>
									<label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
										Logo Upload
									</label>
									<div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50/80 border border-slate-200 text-xs font-medium text-slate-700 shadow-2xs">
										<div className="size-5 rounded bg-emerald-100 flex items-center justify-center">
											<Upload className="size-3 text-[#0f766e]" />
										</div>
										<span className="font-semibold text-slate-800">[Ekutu Logo]</span>
									</div>
								</div>

								{/* Styles */}
								<div>
									<label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
										Styles
									</label>
									<div className="flex items-center gap-2">
										<button
											type="button"
											onClick={() => setActiveStyle('rounded')}
											className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition border cursor-pointer ${
												activeStyle === 'rounded'
													? 'bg-white text-slate-900 border-slate-300 shadow-2xs'
													: 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
											}`}
										>
											Rounded
										</button>
										<button
											type="button"
											onClick={() => setActiveStyle('default')}
											className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition border cursor-pointer ${
												activeStyle === 'default'
													? 'bg-white text-slate-900 border-slate-300 shadow-2xs'
													: 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
											}`}
										>
											Default
										</button>
									</div>
								</div>

								{/* Size Slider */}
								<div>
									<div className="flex items-center justify-between mb-1">
										<label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
											Size
										</label>
										<span className="text-xs text-slate-500 font-mono">
											{qrSize} x {qrSize} px
										</span>
									</div>
									<input
										type="range"
										min="256"
										max="1024"
										step="64"
										value={qrSize}
										onChange={(e) => setQrSize(Number(e.target.value))}
										className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#0f766e]"
									/>
								</div>
							</div>

							{/* QR Display: Real Scannable QR Code */}
							<div className="col-span-6 flex flex-col items-center justify-center">
								<div className="bg-white rounded-3xl p-5 shadow-lg border border-slate-200/90 flex flex-col items-center justify-center w-full aspect-square max-w-[240px] relative overflow-hidden">
									{/* Real Scannable SVG Container */}
									<div
										ref={desktopQrRef}
										className="w-[190px] h-[190px] flex items-center justify-center [&>svg]:w-full [&>svg]:h-full"
									/>
									{/* Center Ekutu Logo Stamp */}
									<div className="absolute size-9 rounded-lg bg-white shadow-md border border-slate-100 flex items-center justify-center p-1 pointer-events-none">
										<EkutuQrLogo size="sm" showText={false} />
									</div>
								</div>
								<div className="mt-3 text-center w-full">
									<p className="text-[11px] text-slate-500 font-medium mb-3">
										Point phone camera to test live redirect
									</p>
									<Link
										href="/create"
										className="w-full inline-flex items-center justify-center gap-2 bg-[#0f766e] hover:bg-[#115e59] active:bg-[#134e4a] text-white font-semibold text-xs py-2.5 px-4 rounded-xl shadow-2xs hover:shadow-xs transition-all active:scale-[0.98]"
									>
										<Sparkles className="size-3.5" />
										<span>Create Free QR Code</span>
										<ArrowRight className="size-3.5 ml-0.5" />
									</Link>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Right Card: Live Experience Preview with Dual Phones */}
				<div className="bg-white border border-slate-200/90 rounded-3xl p-7 shadow-sm flex flex-col justify-between">
					<div>
						{/* Card Header with iPhone 13 Badge */}
						<div className="flex items-center justify-between mb-5">
							<h2 className="text-2xl font-bold text-slate-900">Live Experience Preview</h2>
							<div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-xs font-semibold text-slate-700 shadow-2xs">
								<span className="text-slate-900 text-sm"></span>
								<span>iPhone 13</span>
							</div>
						</div>

						{/* Dual Phones Side by Side matching mockup */}
						<div className="flex items-center justify-center gap-5 py-2 overflow-hidden">
							{/* Phone 1: Digital Business Card */}
							<div className="w-[185px] bg-black p-2.5 rounded-[2.3rem] shadow-2xl border border-slate-800 shrink-0">
								<div className="bg-white rounded-[1.8rem] overflow-hidden flex flex-col aspect-[9/18.5] text-center relative border border-slate-100">
									{/* Top Notch & Status Bar */}
									<div className="h-5 px-3 pt-1 flex items-center justify-between text-slate-800 text-[8.5px] font-semibold bg-white shrink-0">
										<span>9:41</span>
										<div className="w-12 h-2.5 bg-black rounded-b-md mx-auto -mt-1" />
										<div className="flex items-center gap-0.5">
											<span className="text-[7.5px]">●●●</span>
											<span className="text-[7.5px]">🔋</span>
										</div>
									</div>

									{/* Emerald Card Header */}
									<div className="bg-gradient-to-b from-[#0f766e] to-[#115e59] text-white pt-2.5 pb-6 px-2 relative shrink-0">
										<div className="text-[10px] font-bold tracking-tight">
											Digital Business Card
										</div>
									</div>

									{/* Profile Section */}
									<div className="relative -mt-5 px-2.5 pb-2 flex flex-col items-center flex-1">
										<div className="size-13 rounded-full border-2 border-white bg-slate-100 overflow-hidden shadow-sm mb-1.5 shrink-0">
											{/* eslint-disable-next-line @next/next/no-img-element */}
											<img
												src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=300&q=80"
												alt="Sarah Jenkins"
												className="size-full object-cover"
											/>
										</div>

										<div className="text-xs font-bold text-slate-900 leading-tight">
											Sarah Jenkins
										</div>
										<div className="text-[8.5px] text-slate-500 font-medium">VP of Marketing</div>
										<div className="text-[8px] text-slate-400 mb-2.5">Acme Corporation</div>

										{/* Interactive Action Buttons */}
										<div className="w-full space-y-1.5 mb-2.5">
											<div className="w-full py-1.5 px-2 rounded-xl bg-slate-50 border border-slate-200 text-[9px] font-semibold text-slate-700 flex items-center justify-center gap-1.5 shadow-2xs">
												<Phone className="size-3 text-[#0f766e]" />
												<span>Call +1 (415) 555-0192</span>
											</div>
											<div className="w-full py-1.5 px-2 rounded-xl bg-slate-50 border border-slate-200 text-[9px] font-semibold text-slate-700 flex items-center justify-center gap-1.5 shadow-2xs">
												<Mail className="size-3 text-[#0f766e]" />
												<span>sarah.j@acmecorp.com</span>
											</div>
										</div>

										{/* Green Send CTA Button */}
										<button
											type="button"
											className="w-full py-1.5 rounded-xl bg-[#0f766e] text-white text-[10px] font-bold shadow-2xs hover:bg-[#115e59] transition flex items-center justify-center gap-1"
										>
											<Share2 className="size-3" />
											<span>Share Contact Back</span>
										</button>

										<div className="mt-2 text-[7.5px] text-slate-400">Ekutu QR</div>
									</div>

									{/* Bottom Home Indicator */}
									<div className="w-12 h-1 bg-slate-300 rounded-full mx-auto mb-1 shrink-0" />
								</div>
							</div>

							{/* Phone 2: Restaurant Menu */}
							<div className="w-[185px] bg-black p-2.5 rounded-[2.3rem] shadow-2xl border border-slate-800 shrink-0">
								<div className="bg-white rounded-[1.8rem] overflow-hidden flex flex-col aspect-[9/18.5] text-left relative border border-slate-100">
									{/* Top Notch & Status Bar */}
									<div className="h-5 px-3 pt-1 flex items-center justify-between text-slate-800 text-[8.5px] font-semibold bg-white shrink-0">
										<span>9:41</span>
										<div className="w-12 h-2.5 bg-black rounded-b-md mx-auto -mt-1" />
										<div className="flex items-center gap-0.5">
											<span className="text-[7.5px]">●●●</span>
											<span className="text-[7.5px]">🔋</span>
										</div>
									</div>

									{/* Food Header Photo Banner */}
									<div className="relative h-14 w-full overflow-hidden shrink-0">
										{/* eslint-disable-next-line @next/next/no-img-element */}
										<img
											src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=400&q=80"
											alt="The Olive Bistro"
											className="size-full object-cover"
										/>
										<div className="absolute inset-0 bg-black/40 flex flex-col justify-end p-1.5 text-white">
											<div className="text-[10px] font-extrabold leading-tight">
												The Olive Bistro
											</div>
											<div className="text-[7.5px] text-slate-200">Italian & Contemporary • $$</div>
										</div>
									</div>

									{/* Food Menu Items Stack */}
									<div className="p-2 pt-1.5 space-y-1.5 flex-1 overflow-hidden">
										{/* Item 1 */}
										<div className="flex items-center gap-1.5 pb-1 border-b border-slate-100">
											<div className="size-7 rounded-lg overflow-hidden shrink-0 bg-slate-100">
												{/* eslint-disable-next-line @next/next/no-img-element */}
												<img
													src="https://images.unsplash.com/photo-1551183053-bf91a1d81141?auto=format&fit=crop&w=160&q=80"
													alt="Truffle Tagliatelle"
													className="size-full object-cover"
												/>
											</div>
											<div className="min-w-0 flex-1 pr-1">
												<div className="text-[8.5px] font-bold text-slate-900 truncate">
													Truffle Tagliatelle
												</div>
												<div className="text-[7px] text-slate-400 truncate">
													Fresh black truffles
												</div>
											</div>
											<span className="text-[8.5px] font-bold text-slate-900 shrink-0">$24.00</span>
										</div>

										{/* Item 2 */}
										<div className="flex items-center gap-1.5 pb-1 border-b border-slate-100">
											<div className="size-7 rounded-lg overflow-hidden shrink-0 bg-slate-100">
												{/* eslint-disable-next-line @next/next/no-img-element */}
												<img
													src="https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=160&q=80"
													alt="Burrata Caprese"
													className="size-full object-cover"
												/>
											</div>
											<div className="min-w-0 flex-1 pr-1">
												<div className="text-[8.5px] font-bold text-slate-900 truncate">
													Burrata Caprese
												</div>
												<div className="text-[7px] text-slate-400 truncate">Heirloom tomatoes</div>
											</div>
											<span className="text-[8.5px] font-bold text-slate-900 shrink-0">$17.50</span>
										</div>

										{/* Item 3 */}
										<div className="flex items-center gap-1.5 pb-1 border-b border-slate-100">
											<div className="size-7 rounded-lg overflow-hidden shrink-0 bg-slate-100">
												{/* eslint-disable-next-line @next/next/no-img-element */}
												<img
													src="https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=160&q=80"
													alt="Wood-Fired Pizza"
													className="size-full object-cover"
												/>
											</div>
											<div className="min-w-0 flex-1 pr-1">
												<div className="text-[8.5px] font-bold text-slate-900 truncate">
													Margherita Pizza
												</div>
												<div className="text-[7px] text-slate-400 truncate">Wood-fired basil</div>
											</div>
											<span className="text-[8.5px] font-bold text-slate-900 shrink-0">$19.00</span>
										</div>
									</div>

									{/* Bottom Home Indicator */}
									<div className="w-12 h-1 bg-slate-300 rounded-full mx-auto mb-1 shrink-0" />
								</div>
							</div>
						</div>

						{/* Direct Link to Experience Creator */}
						<div className="mt-5 w-full">
							<Link
								href="/create"
								className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-black text-white font-semibold text-xs py-2.5 px-4 rounded-xl shadow-2xs hover:shadow-xs transition-all active:scale-[0.98]"
							>
								<Sparkles className="size-3.5 text-emerald-400" />
								<span>Create Mobile Experience</span>
								<ArrowRight className="size-3.5 ml-0.5" />
							</Link>
						</div>
					</div>
				</div>
			</div>

			{/* ============================================================== */}
			{/* BOTTOM TRUST / FEATURE BAR                                     */}
			{/* ============================================================== */}
			<div className="mt-8 pt-6 border-t border-slate-200/80 flex flex-wrap items-center justify-center gap-6 sm:gap-14 text-slate-700 text-xs sm:text-sm font-semibold">
				<div className="flex items-center gap-2">
					<div className="size-6 sm:size-7 rounded-full bg-emerald-50 flex items-center justify-center text-[#0f766e]">
						<Clock className="size-3.5 sm:size-4" />
					</div>
					<span>Real-time Tracking</span>
				</div>
				<div className="flex items-center gap-2">
					<div className="size-6 sm:size-7 rounded-full bg-emerald-50 flex items-center justify-center text-[#0f766e]">
						<Users className="size-3.5 sm:size-4" />
					</div>
					<span>Bulk Management</span>
				</div>
				<div className="flex items-center gap-2">
					<div className="size-6 sm:size-7 rounded-full bg-emerald-50 flex items-center justify-center text-[#0f766e]">
						<Sparkles className="size-3.5 sm:size-4" />
					</div>
					<span>100% Free</span>
				</div>
			</div>
		</div>
	);
}
